import AppError from '@/components/AppError';
import { TappFormField } from '@/components/ui/TappFormField';
import { TappInput } from '@/components/ui/TappInput';
import { TappLabel } from '@/components/ui/TappLabel';
import { TappPageHeader } from '@/components/ui/TappPageHeader';
import { TappSelect } from '@/components/ui/TappSelect';
import { Button } from '@/components/ui/button';
import { PhoneInput } from '@/components/ui/phone-input';
import { cn } from '@/components/ui/utils';
import { Customer, UserStatus } from '@/lib/models/user';
import { ApiUtils } from '@/lib/utils/api';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { useAlert } from '@/components/ui/alert-dialog-provider';

const CustomerDetailsPage = () => {
  const { t } = useTranslation();

  return (
    <>
      <TappPageHeader title={t('editCustomer')} defaultBack backLink="/admin/customers" />
      <Details />
    </>
  );
};

const userStatusOptions = [
  { label: 'Activ', value: UserStatus.active },
  { label: 'Suspendat', value: UserStatus.suspended },
  { label: 'Blocat', value: UserStatus.blocked },
];

const customerSchema = z.object({
  firstName: z.string().min(1, 'Prenumele este obligatoriu'),
  lastName: z.string().min(1, 'Numele este obligatoriu'),
  phoneNumber: z
    .string({ required_error: 'Numărul de telefon este obligatoriu' })
    .min(1, 'Numărul de telefon este obligatoriu'),
  status: z.nativeEnum(UserStatus, { required_error: 'Statusul este obligatoriu' }),
});

type CustomerFormData = z.infer<typeof customerSchema>;

const Details = () => {
  const { id } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const alert = useAlert();

  const { data, isLoading, error } = useQuery({
    queryKey: [`customer/${id}`],
    queryFn: () => ApiUtils.get<Customer>(`users/customers/${id}`),
  });

  const { mutateAsync: updateCustomer } = useMutation({
    mutationFn: (data: CustomerFormData) => ApiUtils.put(`users/customers/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      queryClient.invalidateQueries({ queryKey: [`customer/${id}`] });
    },
  });

  const {
    handleSubmit,
    control,
    reset,
    formState: { isSubmitting, errors },
  } = useForm<CustomerFormData>({
    resolver: zodResolver(customerSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      phoneNumber: '',
      status: UserStatus.active,
    },
  });

  useEffect(() => {
    if (data) {
      reset({
        firstName: data.firstName || data.fullName?.split(' ')[0] || '',
        lastName: data.lastName || data.fullName?.split(' ').slice(1).join(' ') || '',
        phoneNumber: data.phoneNumber,
        status: data.status,
      });
    }
  }, [data, reset]);

  const onSubmit = async (formData: CustomerFormData) => {
    try {
      await updateCustomer(formData);
      await alert({
        title: t('success'),
        body: t('customerUpdated'),
      });
      navigate('/admin/customers');
    } catch (err: any) {
      console.error(err);
      await alert({
        title: t('error'),
        body: ApiUtils.convertApiErrorToString(err),
      });
    }
  };

  if (error) return <AppError error={error} />;

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      noValidate
      className={cn('flex w-full max-w-md flex-1 flex-col items-start gap-2', { isLoading })}
    >
      <TappFormField>
        <TappLabel htmlFor="firstName">{t('firstName')}</TappLabel>
        <Controller
          name="firstName"
          control={control}
          render={({ field, fieldState }) => (
            <TappInput className="w-full" placeholder={t('firstName')} {...field} {...fieldState} />
          )}
        />
      </TappFormField>

      <TappFormField>
        <TappLabel htmlFor="lastName">{t('lastName')}</TappLabel>
        <Controller
          name="lastName"
          control={control}
          render={({ field, fieldState }) => (
            <TappInput className="w-full" placeholder={t('lastName')} {...field} {...fieldState} />
          )}
        />
      </TappFormField>

      <TappFormField>
        <TappLabel htmlFor="phoneNumber">{t('phone')}</TappLabel>
        <Controller
          name="phoneNumber"
          control={control}
          render={({ field, fieldState }) => (
            <PhoneInput className="w-full" placeholder="+40741237583" {...field} {...fieldState} />
          )}
        />
      </TappFormField>

      <TappFormField>
        <TappLabel htmlFor="status">{t('status')}</TappLabel>
        <Controller
          name="status"
          control={control}
          render={({ field: { value, onChange, name, onBlur }, fieldState }) => (
            <TappSelect
              className="w-full"
              options={userStatusOptions}
              value={userStatusOptions.find((c) => c.value === value)}
              onChange={(val) => onChange(val?.value)}
              name={name}
              onBlur={onBlur}
              {...fieldState}
            />
          )}
        />
      </TappFormField>

      <div className="mt-4 flex gap-2">
        <Button type="submit" disabled={isSubmitting}>
          {t('save')}
        </Button>
        <Button type="button" variant="outline" onClick={() => navigate('/admin/customers')}>
          {t('cancel')}
        </Button>
      </div>
    </form>
  );
};

export default CustomerDetailsPage;
